<template>
	<div class="login-wrapper">
		<div class="bg-gradient"></div>

		<el-card class="login-card" shadow="always">
			<div class="header">
				<el-image :src="mailinweb" style="width: 190px;" />

				<p class="subtitle">Gestión de Casas de Cambio</p>
			</div>

			<el-form ref="form" :model="model" :rules="rules" label-position="top" @submit.native.prevent="submit" class="form-space">
				<el-form-item label="Nombre de Usuario" prop="username">
					<el-input v-model="model.username" size="large" placeholder="Nombre de Usuario" :prefix-icon="User" autocomplete="username" class="input-like-tailwind" />
				</el-form-item>

				<el-form-item label="Contraseña" prop="password">
					<el-input
						v-model="model.password"
						size="large"
						type="password"
						show-password
						placeholder="Contraseña"
						:prefix-icon="Lock"
						autocomplete="current-password"
						class="input-like-tailwind"
					/>
				</el-form-item>

				<!-- Mostrar sucursales y cajas solo después de validar credenciales -->
				<el-form-item v-if="credentialsValidated" label="Sucursal" prop="sucursal_id">
					<el-select v-model="model.sucursal_id" size="large" placeholder="Seleccione sucursal" @change="searchCajas" class="input-like-tailwind">
						<el-option v-for="sucursal in sucursales.data" :key="sucursal.id" :label="sucursal.nombre" :value="sucursal.id" />
					</el-select>
				</el-form-item>

				<el-form-item v-if="credentialsValidated" label="Caja" prop="caja_id">
					<el-select v-model="model.caja_id" size="large" placeholder="Seleccione caja" :disabled="disabled" class="input-like-tailwind">
						<el-option v-for="option in filteredCajas.data" :key="option.id" :label="option.nombre" :value="option.id" />
					</el-select>
				</el-form-item>

				<el-button v-if="!credentialsValidated" size="large" type="primary" @click="validateCredentials" class="full-width-btn primary-like-tailwind" round>Validar Credenciales</el-button>
				<el-button v-if="credentialsValidated" size="large" type="primary" native-type="submit" class="full-width-btn primary-like-tailwind" round>Iniciar Sesión</el-button>
			</el-form>
		</el-card>
	</div>
</template>

<script setup>
	import { reactive, ref, computed, onMounted, nextTick } from 'vue';
	import { ElLoading, ElMessage } from 'element-plus';
	import { User, Lock } from '@element-plus/icons-vue';

	import mailinweb from '@/assets/svg/aurelius1.svg';

	// Estado
	const filteredCajas = ref({ data: [] });
	const sucursales = ref({ data: [] });
	const credentialsValidated = ref(false);

	// Paso 1: Validar credenciales y obtener opciones
	const validateCredentials = async () => {
		const loading = ElLoading.service();
		try {
			const validateResponse = await fetch('/validate', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					username: model.username,
					password: model.password,
				}),
			});

			const { success, user, sucursales: userSucursales, cajas: userCajas } = await validateResponse.json();

			if (success) {
				credentialsValidated.value = true;

				// Guardar las opciones recibidas del servidor
				sucursales.value = { data: userSucursales };
				filteredCajas.value = { data: userCajas };

				ElMessage({ message: 'Credenciales válidas. Seleccione sucursal y caja.', type: 'success' });

				// No agregar reglas inmediatamente, solo cuando sea necesario
				// updateValidationRules();
			} else {
				ElMessage({ message: 'Credenciales inválidas', type: 'error' });
			}
		} catch (error) {
			ElMessage({ message: 'Error de red. Intente nuevamente.', type: 'error' });
		} finally {
			loading.close();
		}
	};

	// Buscar cajas por sucursal (ahora filtra las cajas ya recibidas)
	const searchCajas = async () => {
		model.caja_id = '';
		try {
			if (!model.sucursal_id) {
				// Si no hay sucursal seleccionada, mostrar todas las cajas disponibles
				return;
			}

			const res = await fetch(`/api/caja/bySucursal/${model.sucursal_id}`);
			filteredCajas.value = await res.json();
			return filteredCajas.value;
		} catch {
			ElMessage({ message: 'No se pudieron cargar las cajas', type: 'error' });
		}
	};

	// Actualizar reglas de validación
	const updateValidationRules = () => {
		if (credentialsValidated.value) {
			// Agregar reglas solo cuando sea necesario
			rules.sucursal_id = [{ required: true, message: 'Seleccione una sucursal', trigger: 'change' }];
			rules.caja_id = [{ required: true, message: 'Seleccione una caja', trigger: 'change' }];
		}
	};

	// Refs y computeds
	const form = ref(null);
	const disabled = computed(() => !model.sucursal_id);

	// Submit
	const submit = async () => {
		// Primero, agregar las reglas de validación si las credenciales ya fueron validadas
		if (credentialsValidated.value) {
			updateValidationRules();
		}

		form.value.validate(async (valid) => {
			if (!valid) {
				ElMessage({ message: 'Verifique los datos del formulario', type: 'error' });
				return;
			}

			const loading = ElLoading.service();
			try {
				let res = await fetch('/login', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify(model),
				});

				if (res.status === 200) {
					await res.json();
					window.location = '/';
				} else {
					const json = await res.json();
					ElMessage({ message: json.error, type: 'error' });
				}
			} catch {
				ElMessage({ message: 'Error de red. Intente nuevamente.', type: 'error' });
			} finally {
				loading.close();
			}
		});
	};

	// Modelo
	const model = reactive({
		username: '',
		password: '',
		sucursal_id: '',
		caja_id: '',
	});

	// Reglas de validación (inicialmente solo para credenciales)
	const rules = reactive({
		username: [{ required: true, message: 'Ingrese usuario', trigger: 'blur' }],
		password: [{ required: true, message: 'Ingrese contraseña', trigger: 'blur' }],
		// sucursal_id y caja_id se agregan dinámicamente después de validar credenciales
	});
</script>

<style scoped>
	/* Tipografías como en tu HTML */
	@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;600;700&family=Roboto:wght@400;500&display=swap');
	
	.login-wrapper {
		position: relative;
		min-height: 100dvh; /* mejor manejo en móviles */
		display: grid;
		place-items: center;
		padding: 1rem;
		overflow: hidden;
		font-family: 'Roboto', system-ui, -apple-system, Segoe UI, Helvetica, Arial, sans-serif;
	}

	/* Gradiente EXACTO del HTML (sin modo oscuro) */
	.bg-gradient {
		position: absolute;
		inset: 0;
		background: linear-gradient(135deg, #fce7f3 0%, #e0e7ff 50%, #d1fae5 100%);
		z-index: 0;
	}

	/* Tarjeta blanca, sin tintes morados/azules añadidos */
	.login-card {
		width: 100%;
		max-width: 480px; /* similar a max-w-md */
		z-index: 1;
		border-radius: 12px; /* rounded-xl */
		border: 1px solid rgba(229, 231, 235, 0.9); /* gray-200-ish */
		background: rgba(255, 255, 255, 0.96); /* casi blanco como en la captura */
		backdrop-filter: blur(10px);
		/* sombra suave neutra, sin azul */
		box-shadow: 0 10px 15px rgba(2, 6, 23, 0.06), 0 25px 40px rgba(2, 6, 23, 0.12);
		padding: 2rem 2rem 1.5rem;
	}

	.header {
		text-align: center;
		margin-bottom: 1.75rem;
	}

	.brand {
		font-family: 'Cormorant Garamond', serif;
		font-weight: 700;
		font-size: 3.25rem; /* ~text-5xl/6xl */
		line-height: 1.1;
		letter-spacing: 0.12em; /* tracking-wider */
		color: #0f172a; /* slate-900 */
		margin: 0;
	}

	.subtitle {
		color: #6b7280; /* gray-500/600 */
		margin-top: 0.5rem;
		font-size: 0.95rem;
	}

	/* Separación vertical entre items (~space-y-6) */
	.form-space :deep(.el-form-item) + :deep(.el-form-item) {
		margin-top: 1.25rem;
	}

	/* Inputs estilo del HTML (borde gris claro, sin sombras azules) */
	.input-like-tailwind :deep(.el-input__wrapper),
	.input-like-tailwind :deep(.el-select__wrapper) {
		padding: 12px 12px;
		border-radius: 10px;
		background: #ffffff;
		border: 1px solid #d1d5db; /* gray-300 */
		box-shadow: none;
		transition: box-shadow 0.2s, border-color 0.2s, background-color 0.2s;
		height: 48px !important;
		min-height: 48px !important;
		box-sizing: border-box;
	}

	.input-like-tailwind :deep(.el-input__inner),
	.input-like-tailwind :deep(.el-select__selected-item) {
		color: #111827; /* gray-900 */
		font-size: 14px;
		height: 22px !important;
		line-height: 1.5;
		padding: 0;
		margin: 0;
	}

	.input-like-tailwind :deep(.el-input__inner) {
		background: transparent;
		border: none;
		outline: none;
	}

	.input-like-tailwind :deep(.el-select__selection) {
		height: 22px !important;
		display: flex;
		align-items: center;
	}

	.input-like-tailwind :deep(.el-select .el-select__placeholder) {
		color: #9ca3af !important; /* gray-400 */
		font-size: 14px !important;
		font-weight: normal !important;
	}

	.input-like-tailwind :deep(.el-select__placeholder) {
		color: #9ca3af !important; /* gray-400 */
		font-size: 14px !important;
		font-weight: normal !important;
	}

	.input-like-tailwind :deep(.el-input__inner::placeholder) {
		color: #9ca3af !important; /* gray-400 */
		font-size: 14px !important;
		font-weight: normal !important;
	}

	.input-like-tailwind :deep(.el-icon) {
		color: #9ca3af; /* gray-400 */
	}

	.input-like-tailwind :deep(.el-select__caret) {
		color: #9ca3af; /* gray-400 */
	}

	.input-like-tailwind :deep(.is-focus .el-input__wrapper),
	.input-like-tailwind :deep(.is-focused .el-select__wrapper) {
		border-color: #3b82f6; /* blue-500 */
		box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.25); /* focus ring */
	}

	/* Asegurar que el select tenga la misma apariencia que los inputs */
	.input-like-tailwind :deep(.el-select__wrapper) {
		display: flex;
		align-items: center;
		width: 100%;
		position: relative;
		cursor: pointer;
		height: 48px !important;
		min-height: 48px !important;
	}

	.input-like-tailwind :deep(.el-select__selection) {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		width: 100%;
		height: 22px !important;
		min-height: auto;
	}

	.input-like-tailwind :deep(.el-select__selected-item) {
		flex: 1;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		height: 22px !important;
		line-height: 22px !important;
		display: flex;
		align-items: center;
	}

	.input-like-tailwind :deep(.el-select__placeholder) {
		height: 22px !important;
		line-height: 22px !important;
		display: flex;
		align-items: center;
		color: #9ca3af !important; /* gray-400 */
		font-size: 14px !important;
		font-weight: normal !important;
	}


	.forgot-link {
		font-weight: 500;
		color: #3b82f6;
	}

	/* Botón principal azul como en tu HTML */
	.full-width-btn {
		width: 100%;
		height: 44px;
	}
	.primary-like-tailwind {
		--btn-primary: #3b82f6; /* blue-500 */
		background: var(--btn-primary);
		border-color: var(--btn-primary);
	}
	.primary-like-tailwind:hover {
		background: #2563eb; /* blue-600 */
		border-color: #2563eb;
	}
</style>
